import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Plus, X, Clock, MapPin, Phone, Mail, Calendar } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { useImageStorage } from '@/hooks/useImageStorage';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

// Mock data for editing - in real app this would come from API/database
const mockServiceData = {
  201: {
    id: 201,
    name: "Personal Marketing Coaching",
    description: "Help ondernemers hun marketing strategie te ontwikkelen en meer klanten te krijgen.",
    price: "85",
    duration: "60",
    category: "Business & Consulting",
    location: "both",
    availability: {
      days: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag"],
      times: ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"]
    },
    contactPreference: "both-contact",
    tags: ["Marketing", "Coaching", "Strategie"],
    images: [] as ImageFile[],
    additionalInfo: "Breng je eigen laptop mee voor de praktische oefeningen. We werken samen aan je unieke marketing plan."
  },
  202: {
    id: 202,
    name: "Content Creation Workshop",
    description: "Workshop over het maken van content die converteert voor social media en websites.",
    price: "150",
    duration: "180",
    category: "Creative Services",
    location: "in-person",
    availability: {
      days: ["Zaterdag", "Zondag"],
      times: ["10:00", "11:00", "14:00", "15:00"]
    },
    contactPreference: "email",
    tags: ["Content", "Workshop", "Social Media"],
    images: [] as ImageFile[],
    additionalInfo: "Workshop inclusief lunch en alle materialen. Maximaal 8 deelnemers voor persoonlijke aandacht."
  },
  203: {
    id: 203,
    name: "LinkedIn Profile Optimization",
    description: "Professionele LinkedIn profiel optimalisatie om meer opportunities te krijgen.",
    price: "75",
    duration: "45",
    category: "Professional Services",
    location: "online",
    availability: {
      days: ["Maandag", "Woensdag", "Vrijdag"],
      times: ["19:00", "20:00"]
    },
    contactPreference: "phone",
    tags: ["LinkedIn", "Profiel", "Optimalisatie"],
    images: [] as ImageFile[],
    additionalInfo: "Stuur je huidige LinkedIn profiel URL door voor optimale voorbereiding."
  }
};
const AddService = () => {
  const navigate = useNavigate();
  const { serviceId } = useParams(); // For edit mode: /add-service/edit/:serviceId
  const isEditMode = Boolean(serviceId);
  
  const { images: storedImages, updateImages, clearImages } = useImageStorage(
    isEditMode ? `service-images-edit-${serviceId}` : 'service-images'
  );
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: '',
    location: '',
    availability: {
      days: [] as string[],
      times: [] as string[]
    },
    contactPreference: '',
    tags: [] as string[],
    images: storedImages,
    additionalInfo: ''
  });
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(isEditMode);

  // Load existing service data in edit mode
  useEffect(() => {
    if (isEditMode && serviceId) {
      setIsLoading(true);
      
      // Simulate API call - in real app this would be an actual API call
      setTimeout(() => {
        const existingService = mockServiceData[serviceId as keyof typeof mockServiceData];
        
        if (existingService) {
          setFormData({
            name: existingService.name,
            description: existingService.description,
            price: existingService.price,
            duration: existingService.duration,
            category: existingService.category,
            location: existingService.location,
            availability: existingService.availability,
            contactPreference: existingService.contactPreference,
            tags: existingService.tags,
            images: storedImages, // Use stored images or existing images
            additionalInfo: existingService.additionalInfo
          });
          
          console.log('Loaded service data for editing:', existingService);
          toast.info('Service data geladen voor bewerking');
        } else {
          toast.error('Service niet gevonden');
          navigate('/marketplace');
        }
        
        setIsLoading(false);
      }, 500); // Simulate network delay
    }
  }, [isEditMode, serviceId, navigate, storedImages]);

  const serviceCategories = [
    'Business & Consulting',
    'Creative Services',
    'Tech & Digital',
    'Professional Services',
    'Lifestyle & Wellness',
    'Education & Training'
  ];

  const durationOptions = [
    { value: '30', label: '30 minuten' },
    { value: '45', label: '45 minuten' },
    { value: '60', label: '1 uur' },
    { value: '90', label: '1.5 uur' },
    { value: '120', label: '2 uur' },
    { value: '180', label: '3 uur' },
    { value: 'custom', label: 'Aangepast' }
  ];

  const weekDays = [
    'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'
  ];

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvailabilityChange = (type: 'days' | 'times', value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [type]: checked 
          ? [...prev.availability[type], value]
          : prev.availability[type].filter(item => item !== value)
      }
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImagesChange = (images: ImageFile[]) => {
    setFormData(prev => ({
      ...prev,
      images
    }));
    updateImages(images);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.description || !formData.price || !formData.category || !formData.duration) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    if (formData.availability.days.length === 0) {
      toast.error('Selecteer minimaal één dag beschikbaarheid');
      return;
    }

    console.log(`${isEditMode ? 'Updating' : 'Creating'} service:`, {
      ...formData,
      imageCount: formData.images.length,
      imageNames: formData.images.map(img => img.name),
      serviceId: isEditMode ? serviceId : 'new'
    });

    if (isEditMode) {
      toast.success(`Service "${formData.name}" succesvol bijgewerkt!`);
    } else {
      if (formData.images.length > 0) {
        toast.success(`Dienst succesvol toegevoegd met ${formData.images.length} foto${formData.images.length === 1 ? '' : 's'}!`);
      } else {
        toast.success('Dienst succesvol toegevoegd!');
      }
    }

    // Clear stored images after successful submission
    clearImages();
    
    // Navigate back to marketplace or service detail
    if (isEditMode) {
      navigate(`/service/${serviceId}`);
    } else {
      navigate('/marketplace');
    }
  };

  // Update form when stored images change
  useEffect(() => {
    setFormData(prev => ({ ...prev, images: storedImages }));
  }, [storedImages]);

  // Show loading state in edit mode
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f6f1eb' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: '#a0897c' }}></div>
          <p style={{ color: '#b6a092' }}>Service data laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#f6f1eb' }}>
      {/* Header */}
      <div className="bg-white shadow-sm" style={{ borderBottom: '1px solid #efe7dd' }}>
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/50"
              style={{ color: '#837069' }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold" style={{ color: '#837069' }}>
                {isEditMode ? 'Dienst Bewerken' : 'Dienst Plaatsen'}
              </h1>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                {isEditMode 
                  ? 'Pas je dienst aan en sla de wijzigingen op' 
                  : 'Deel je expertise met de WOM community'
                }
              </p>
            </div>
          </div>

          {/* Service Header */}
          <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-2xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>
                    {isEditMode ? 'Dienst Bewerken' : 'Nieuwe Dienst'}
                  </span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                {isEditMode 
                  ? 'Verfijn je aanbod voor de WOM community' 
                  : 'Deel je talent met vrouwen die jouw expertise nodig hebben'
                }
              </p>
            </div>
          </div>
          {/* Edit Mode Badge */}
          {isEditMode && (
            <div className="mt-3">
              <Badge variant="outline" className="text-xs" style={{ backgroundColor: '#fbf8f4', color: '#a0897c', borderColor: '#efe7dd' }}>
                <Clock className="h-3 w-3 mr-1" />
                Bewerken: {formData.name || 'Service'}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Card */}
          <Card style={{ border: '1px solid #efe7dd' }}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                <Plus className="h-5 w-5" style={{ color: '#a0897c' }} />
                Basis Informatie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name" style={{ color: '#837069' }}>Dienst Naam *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Bijv. Social Media Strategie Sessie"
                  className="mt-1"
                  style={{ borderColor: '#efe7dd' }}
                />
              </div>

              <div>
                <Label htmlFor="description" style={{ color: '#837069' }}>Beschrijving *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Beschrijf je dienst, wat ga je doen voor je klant?"
                  className="mt-1 h-24"
                  style={{ borderColor: '#efe7dd' }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price" style={{ color: '#837069' }}>Prijs (€) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="75.00"
                    className="mt-1"
                    style={{ borderColor: '#efe7dd' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#837069' }}>Duur *</Label>
                  <Select value={formData.duration} onValueChange={(value) => handleInputChange('duration', value)}>
                    <SelectTrigger className="mt-1" style={{ borderColor: '#d9cbbf', backgroundColor: '#efe7dd' }}>
                      <SelectValue placeholder="Selecteer duur" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                      {durationOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value} style={{ backgroundColor: '#efe7dd' }}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label style={{ color: '#837069' }}>Categorie *</Label>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger className="mt-1" style={{ borderColor: '#d9cbbf', backgroundColor: '#efe7dd' }}>
                    <SelectValue placeholder="Kies een categorie" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                    {serviceCategories.map((category) => (
                      <SelectItem key={category} value={category} style={{ backgroundColor: '#efe7dd' }}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Location & Delivery Card */}
          <Card style={{ border: '1px solid #efe7dd' }}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                <MapPin className="h-5 w-5" style={{ color: '#a0897c' }} />
                Locatie & Levering
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label style={{ color: '#837069' }}>Hoe lever je je dienst?</Label>
                <RadioGroup 
                  value={formData.location} 
                  onValueChange={(value) => handleInputChange('location', value)}
                  className="mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="online" id="online" />
                    <Label htmlFor="online" style={{ color: '#837069' }}>Online (Zoom, Teams, etc.)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="in-person" id="in-person" />
                    <Label htmlFor="in-person" style={{ color: '#837069' }}>Op locatie / In-persoon</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="both" id="both" />
                    <Label htmlFor="both" style={{ color: '#837069' }}>Beide mogelijk</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Availability Card */}
          <Card style={{ border: '1px solid #efe7dd' }}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                <Calendar className="h-5 w-5" style={{ color: '#a0897c' }} />
                Beschikbaarheid
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label style={{ color: '#837069' }}>Welke dagen ben je beschikbaar? *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {weekDays.map((day) => (
                    <div key={day} className="flex items-center space-x-2">
                      <Checkbox
                        id={day}
                        checked={formData.availability.days.includes(day)}
                        onCheckedChange={(checked) => 
                          handleAvailabilityChange('days', day, checked as boolean)
                        }
                      />
                      <Label htmlFor={day} className="text-sm" style={{ color: '#837069' }}>{day}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label style={{ color: '#837069' }}>Voorkeur tijden</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {timeSlots.map((time) => (
                    <div key={time} className="flex items-center space-x-2">
                      <Checkbox
                        id={time}
                        checked={formData.availability.times.includes(time)}
                        onCheckedChange={(checked) => 
                          handleAvailabilityChange('times', time, checked as boolean)
                        }
                      />
                      <Label htmlFor={time} className="text-sm" style={{ color: '#837069' }}>{time}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Preferences Card */}
          <Card style={{ border: '1px solid #efe7dd' }}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#837069' }}>
                <Phone className="h-5 w-5" style={{ color: '#a0897c' }} />
                Contact Voorkeur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label style={{ color: '#837069' }}>Hoe kunnen klanten je bereiken?</Label>
                <RadioGroup 
                  value={formData.contactPreference} 
                  onValueChange={(value) => handleInputChange('contactPreference', value)}
                  className="mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="email" id="email" />
                    <Label htmlFor="email" className="flex items-center gap-2" style={{ color: '#837069' }}>
                      <Mail className="h-4 w-4" />
                      Email voorkeur
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="phone" id="phone" />
                    <Label htmlFor="phone" className="flex items-center gap-2" style={{ color: '#837069' }}>
                      <Phone className="h-4 w-4" />
                      Telefonisch contact
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="both-contact" id="both-contact" />
                    <Label htmlFor="both-contact" style={{ color: '#837069' }}>Beide mogelijk</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Tags Card */}
          <Card style={{ border: '1px solid #efe7dd' }}>
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: '#837069' }}>Tags & Specialisaties</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Bijv. marketing, strategie, SEO..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  style={{ borderColor: '#efe7dd' }}
                />
                <Button 
                  type="button" 
                  onClick={addTag} 
                  size="sm"
                  style={{ backgroundColor: '#a0897c', color: 'white' }}
                  className="hover:opacity-90"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1" style={{ backgroundColor: '#efe7dd', color: '#837069' }}>
                      {tag}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Info Card */}
          <Card style={{ border: '1px solid #efe7dd' }}>
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: '#837069' }}>Extra Informatie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="additionalInfo" style={{ color: '#837069' }}>Aanvullende informatie</Label>
                <Textarea
                  id="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                  placeholder="Wat moeten klanten nog meer weten? Voorbereidingen, materialen, etc."
                  className="mt-1 h-20"
                  style={{ borderColor: '#efe7dd' }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Images Card */}
          <Card style={{ border: '1px solid #efe7dd' }}>
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: '#837069' }}>Foto's & Media</CardTitle>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Laat potentiële klanten zien wat je doet en hoe je werkt
              </p>
            </CardHeader>
            <CardContent>
              <ImageUpload
                images={formData.images}
                onImagesChange={handleImagesChange}
                maxImages={5}
                maxSizeInMB={5}
              />
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="flex-1 hover:bg-white/50"
              style={{ borderColor: '#efe7dd', color: '#837069' }}
            >
              Annuleren
            </Button>
            <Button 
              type="submit" 
              className="flex-1 hover:opacity-90"
              style={{ backgroundColor: '#a0897c', color: 'white' }}
            >
              {isEditMode ? 'Wijzigingen Opslaan' : 'Dienst Publiceren'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddService;